import React, { useMemo, useState, Fragment } from 'react';
import { Loan, Currency } from '../types';
import { Button } from './ui/Button';
import { Input, Label, Select } from './ui/Forms';
import { Trash2, Power, Landmark, Pencil, X, Calculator, Save, Edit2 } from 'lucide-react';
import { calculateMonthlyPayment, calculateTotalInterest } from '../utils/loans';
import { formatCurrency } from '../utils/currency';
import { CURRENCIES } from '../utils/countries';
interface LoanListProps {
  loans: Loan[];
  partnerNames?: [string, string];
  onDelete: (id: string) => void;
  onUpdate?: (id: string, updates: Partial<Omit<Loan, 'id' | 'createdAt'>>) => void;
  onToggleActive: (id: string) => void;
  onExport?: () => void;
}
{}
function LoanEditForm({
  loan,
  partnerNames,
  onSave,
  onCancel
}: {
  loan: Loan;
  partnerNames: [string, string];
  onSave: (id: string, updates: Partial<Omit<Loan, 'id' | 'createdAt'>>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(loan.name);
  const [person, setPerson] = useState(loan.person);
  const [totalAmount, setTotalAmount] = useState(loan.totalAmount.toString());
  const [depositAmount, setDepositAmount] = useState(loan.depositAmount.toString());
  const [currency, setCurrency] = useState<Currency>(loan.currency);
  const [interestRate, setInterestRate] = useState(loan.interestRate.toString());
  const [durationMonths, setDurationMonths] = useState(loan.durationMonths.toString());
  const [startYear, setStartYear] = useState(loan.startYear.toString());
  const [repaymentType, setRepaymentType] = useState<Loan['repaymentType']>(loan.repaymentType);
  const previewLoan = useMemo<Loan>(() => ({
    ...loan,
    name: name || loan.name,
    person,
    totalAmount: Number(totalAmount) || 0,
    depositAmount: Number(depositAmount) || 0,
    interestRate: Number(interestRate) || 0,
    durationMonths: Number(durationMonths) || 0,
    startYear: Number(startYear) || 0,
    currency,
    repaymentType
  }), [name, person, totalAmount, depositAmount, interestRate, durationMonths, startYear, currency, repaymentType, loan]);
  const monthlyPayment = calculateMonthlyPayment(previewLoan);
  const totalInterest = calculateTotalInterest(previewLoan);
  const principal = Math.max(0, previewLoan.totalAmount - previewLoan.depositAmount);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const totalVal = Number(totalAmount);
    const depositVal = Number(depositAmount);
    const rateVal = Number(interestRate);
    const durationVal = Number(durationMonths);
    const startYearVal = Number(startYear);
    if (name.trim() && !isNaN(totalVal) && !isNaN(depositVal) && !isNaN(rateVal) && !isNaN(durationVal) && !isNaN(startYearVal)) {
      onSave(loan.id, {
        name: name.trim(),
        person,
        totalAmount: totalVal,
        depositAmount: depositVal,
        interestRate: rateVal,
        durationMonths: durationVal,
        startYear: startYearVal,
        currency,
        repaymentType
      });
    }
  };
  return <tr className="block md:table-row border-b border-slate-100 last:border-0" data-id="element-921">
      <td colSpan={5} className="block md:table-cell p-0" data-id="element-922">
        <div className="bg-blue-50/50 border-y border-blue-200 px-4 py-5" data-id="element-923">
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-924">
            <div className="flex items-center justify-between mb-2" data-id="element-925">
              <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2" data-id="element-926">
                <Pencil className="w-4 h-4 text-blue-600" data-id="element-927" />
                Editing: {loan.name}
              </h4>
              <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-928">
                <X className="w-5 h-5" data-id="element-929" />
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-930">
              <div className="col-span-2" data-id="element-931">
                <Label data-id="element-932">Loan Name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Mortgage" required data-id="element-933" />
              </div>
              <div data-id="element-934">
                <Label data-id="element-935">Owner</Label>
                <Select value={person} onChange={e => setPerson(e.target.value)} options={[{
                value: 'Joint',
                label: 'Joint (50/50)'
              }, {
                value: partnerNames[0],
                label: partnerNames[0]
              }, {
                value: partnerNames[1],
                label: partnerNames[1]
              }]} data-id="element-936" />
              </div>
              <div data-id="element-937">
                <Label data-id="element-938">Currency</Label>
                <Select searchable value={currency} onChange={e => setCurrency(e.target.value as Currency)} options={CURRENCIES.map(c => ({
                value: c.code,
                label: `${c.flag} ${c.code} (${c.symbol})`
              }))} data-id="element-939" />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-940">
              <div data-id="element-941">
                <Label data-id="element-942">Total Amount</Label>
                <Input type="number" min="0" step="1000" value={totalAmount} onChange={e => setTotalAmount(e.target.value)} required data-id="element-943" />
              </div>
              <div data-id="element-944">
                <Label data-id="element-945">Down Payment / Deposit</Label>
                <Input type="number" min="0" step="1000" value={depositAmount} onChange={e => setDepositAmount(e.target.value)} data-id="element-946" />
              </div>
              <div data-id="element-947">
                <Label data-id="element-948">Interest Rate (%)</Label>
                <Input type="number" min="0" step="0.01" value={interestRate} onChange={e => setInterestRate(e.target.value)} required data-id="element-949" />
              </div>
              <div data-id="element-950">
                <Label data-id="element-951">Repayment Type</Label>
                <Select value={repaymentType} onChange={e => setRepaymentType(e.target.value as Loan['repaymentType'])} options={[{
                value: 'annuity',
                label: 'Annuity (Fixed)'
              }, {
                value: 'linear',
                label: 'Linear (Decreasing)'
              }, {
                value: 'interest-only',
                label: 'Interest Only'
              }]} data-id="element-952" />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-953">
              <div data-id="element-954">
                <Label data-id="element-955">Duration (Months)</Label>
                <div className="relative" data-id="element-956">
                  <Input type="number" min="1" step="1" value={durationMonths} onChange={e => setDurationMonths(e.target.value)} required data-id="element-957" />
                  <span className="absolute right-3 top-2 text-xs text-slate-400 pointer-events-none" data-id="element-958">
                    {durationMonths ? `${(Number(durationMonths) / 12).toFixed(1)} yrs` : ''}
                  </span>
                </div>
              </div>
              <div data-id="element-959">
                <Label data-id="element-960">Start Year (from now)</Label>
                <Input type="number" min="0" step="1" value={startYear} onChange={e => setStartYear(e.target.value)} required data-id="element-961" />
              </div>
            </div>

            {/* Preview */}
            {principal > 0 && Number(durationMonths) > 0 && <div className="p-4 bg-white rounded-xl border border-slate-200 flex flex-col gap-3" data-id="element-962">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700" data-id="element-963">
                  <Calculator className="w-4 h-4 text-blue-500" data-id="element-964" />
                  Updated Summary
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm" data-id="element-965">
                  <div data-id="element-966">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-967">Principal</p>
                    <p className="font-medium text-slate-900" data-id="element-968">
                      {formatCurrency(principal, currency)}
                    </p>
                  </div>
                  <div data-id="element-969">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-970">
                      Monthly Payment
                    </p>
                    <p className="font-bold text-blue-700" data-id="element-971">
                      {formatCurrency(monthlyPayment, currency)}
                    </p>
                  </div>
                  <div data-id="element-972">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-973">
                      Total Interest
                    </p>
                    <p className="font-medium text-rose-600" data-id="element-974">
                      {formatCurrency(totalInterest, currency)}
                    </p>
                  </div>
                  <div data-id="element-975">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-976">Total Cost</p>
                    <p className="font-medium text-slate-900" data-id="element-977">
                      {formatCurrency(principal + totalInterest, currency)}
                    </p>
                  </div>
                </div>
              </div>}

            <div className="flex items-center gap-3 pt-2" data-id="element-978">
              <Button type="submit" data-id="element-979">
                <Save className="w-4 h-4 mr-2" data-id="element-980" />
                Save Changes
              </Button>
              <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-981">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </td>
    </tr>;
}
function LoanRow({
  loan,
  partnerNames,
  onDelete,
  onEdit,
  onToggleActive
}: {
  loan: Loan;
  partnerNames: [string, string];
  onDelete: (id: string) => void;
  onEdit?: () => void;
  onToggleActive: (id: string) => void;
}) {
  const principal = Math.max(0, loan.totalAmount - loan.depositAmount);
  const monthlyPayment = calculateMonthlyPayment(loan);
  const isActive = loan.isActive;
  const isFirstPartner = loan.person === partnerNames[0];
  const isJoint = loan.person === 'Joint';
  return <>
      {/* Mobile View */}
      <tr className={`md:hidden block border-b border-slate-100 p-4 ${!isActive ? 'opacity-50' : ''}`} data-id="element-982">
        <td className="block p-0" data-id="element-983">
          <div className="flex flex-col gap-3" data-id="element-984">
            <div className="flex justify-between items-start gap-4" data-id="element-985">
              <div className="flex flex-col" data-id="element-986">
                <span className="font-medium text-slate-900 flex items-center gap-1.5" data-id="element-987">
                  {loan.name}
                  {!isActive && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-988">
                      INACTIVE
                    </span>}
                </span>
                <span className="text-xs text-slate-500 mt-0.5" data-id="element-989">
                  {loan.interestRate}% · {loan.durationMonths} mos ·{' '}
                  {loan.repaymentType}
                </span>
              </div>
              <div className="flex flex-col items-end" data-id="element-990">
                <span className="font-bold text-rose-600" data-id="element-991">
                  -{formatCurrency(monthlyPayment, loan.currency)}/mo
                </span>
                <span className="text-xs font-medium text-slate-900 mt-0.5" data-id="element-992">
                  Bal: {formatCurrency(principal, loan.currency)}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-slate-50" data-id="element-993">
              <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${isJoint ? 'bg-teal-50 text-teal-700' : isFirstPartner ? 'bg-indigo-50 text-indigo-700' : 'bg-fuchsia-50 text-fuchsia-700'}`} data-id="element-994">
                {isJoint ? '🤝 Mutual' : loan.person}
              </span>
              <div className="flex items-center gap-1" data-id="element-995">
                <Button variant="ghost" size="sm" onClick={() => onToggleActive(loan.id)} className="px-2 text-slate-400" data-id="element-996">
                  <Power className="w-4 h-4" data-id="element-997" />
                </Button>
                {onEdit && <Button variant="ghost" size="sm" onClick={onEdit} className="px-2 text-slate-400" data-id="element-998">
                    <Edit2 className="w-4 h-4" data-id="element-999" />
                  </Button>}
                <Button variant="ghost" size="sm" onClick={() => onDelete(loan.id)} className="px-2 text-slate-400" data-id="element-1000">
                  <Trash2 className="w-4 h-4" data-id="element-1001" />
                </Button>
              </div>
            </div>
          </div>
        </td>
      </tr>

      {/* Desktop View */}
      <tr className={`hidden md:table-row hover:bg-slate-50/50 transition-colors group border-b border-slate-100 last:border-0 ${!isActive ? 'opacity-50' : ''}`} data-id="element-1002">
        <td className="px-4 py-4 font-medium text-slate-900" data-id="element-1003">
          <div className="flex flex-col" data-id="element-1004">
            <div className="flex items-center gap-2" data-id="element-1005">
              <span data-id="element-1006">{loan.name}</span>
              {!isActive && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-1007">
                  INACTIVE
                </span>}
            </div>
            {loan.createdAt && <span className="text-xs text-slate-400 font-normal mt-0.5" data-id="element-1008">
                Added {new Date(loan.createdAt).toLocaleDateString()}
              </span>}
          </div>
        </td>
        <td className="px-4 py-4 text-slate-600" data-id="element-1009">
          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${isJoint ? 'bg-teal-50 text-teal-700' : isFirstPartner ? 'bg-indigo-50 text-indigo-700' : 'bg-fuchsia-50 text-fuchsia-700'}`} data-id="element-1010">
            {isJoint ? '🤝 Mutual' : loan.person}
          </span>
        </td>
        <td className="px-4 py-4 text-slate-600" data-id="element-1011">
          <div className="flex flex-col" data-id="element-1012">
            <span className="font-medium text-slate-900" data-id="element-1013">
              {formatCurrency(principal, loan.currency)}
            </span>
            <span className="text-xs text-slate-400 mt-0.5" data-id="element-1014">
              {loan.interestRate}% · {loan.durationMonths} mos ·{' '}
              {loan.repaymentType}
              {loan.startYear > 0 ? ` · Starts Yr ${loan.startYear}` : ''}
            </span>
          </div>
        </td>
        <td className="px-4 py-4 text-right" data-id="element-1015">
          <div className="flex flex-col items-end" data-id="element-1016">
            <span className="font-bold text-rose-600" data-id="element-1017">
              -{formatCurrency(monthlyPayment, loan.currency)}/mo
            </span>
            <span className="text-xs text-slate-400 mt-0.5" data-id="element-1018">
              Total Int:{' '}
              {formatCurrency(calculateTotalInterest(loan), loan.currency)}
            </span>
          </div>
        </td>
        <td className="px-4 py-4 text-center" data-id="element-1019">
          <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1020">
            <Button variant="ghost" size="sm" onClick={() => onToggleActive(loan.id)} className={`px-2 ${isActive ? 'text-slate-400 hover:text-amber-600' : 'text-amber-500 hover:text-emerald-600 !opacity-100'}`} title={isActive ? 'Deactivate' : 'Reactivate'} data-id="element-1021">
              <Power className="w-4 h-4" data-id="element-1022" />
            </Button>
            {onEdit && <Button variant="ghost" size="sm" onClick={onEdit} className="text-slate-400 hover:text-indigo-600 px-2" title="Edit" data-id="element-1023">
                <Edit2 className="w-4 h-4" data-id="element-1024" />
              </Button>}
            <Button variant="ghost" size="sm" onClick={() => onDelete(loan.id)} className="text-slate-400 hover:text-red-600 px-2" title="Delete" data-id="element-1025">
              <Trash2 className="w-4 h-4" data-id="element-1026" />
            </Button>
          </div>
        </td>
      </tr>
    </>;
}
export function LoanList({
  loans,
  partnerNames = ['Partner 1', 'Partner 2'],
  onDelete,
  onUpdate,
  onToggleActive,
  onExport
}: LoanListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  if (loans.length === 0) {
    return <div className="h-full flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-1027">
        <div className="p-8 text-center text-slate-500 flex flex-col items-center justify-center h-full min-h-[300px]" data-id="element-1028">
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3" data-id="element-1029">
            <Landmark className="w-6 h-6 text-slate-400" data-id="element-1030" />
          </div>
          <p data-id="element-1031">No loans added yet.</p>
          <p className="text-sm mt-1" data-id="element-1032">
            Add mortgages, car loans, or other debts to include their monthly
            payments in your projections.
          </p>
        </div>
      </div>;
  }
  return <div className="h-full flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-1033">
      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-slate-100" data-id="element-1034">
        {loans.map(loan => {
        const principal = Math.max(0, loan.totalAmount - loan.depositAmount);
        const monthlyPayment = calculateMonthlyPayment(loan);
        const isActive = loan.isActive;
        const isFirstPartner = loan.person === partnerNames[0];
        const isJoint = loan.person === 'Joint';
        if (editingId === loan.id && onUpdate) {
          return <div key={loan.id} className="p-4" data-id="element-1035">
                <LoanEditForm loan={loan} partnerNames={partnerNames} onSave={(id, updates) => {
              onUpdate(id, updates);
              setEditingId(null);
            }} onCancel={() => setEditingId(null)} data-id="element-1036" />
              </div>;
        }
        return <div key={loan.id} className={`p-4 ${!isActive ? 'opacity-50' : ''}`} data-id="element-1037">
              <div className="flex items-start justify-between mb-2" data-id="element-1038">
                <div className="min-w-0 flex-1 mr-3" data-id="element-1039">
                  <div className="flex items-center gap-2" data-id="element-1040">
                    <p className="font-medium text-slate-900 truncate" data-id="element-1041">
                      {loan.name}
                    </p>
                    {!isActive && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-1042">
                        INACTIVE
                      </span>}
                  </div>
                  {loan.createdAt && <p className="text-xs text-slate-400 mt-0.5" data-id="element-1043">
                      Added {new Date(loan.createdAt).toLocaleDateString()}
                    </p>}
                </div>
                <div className="text-right shrink-0" data-id="element-1044">
                  <p className="font-bold text-rose-600" data-id="element-1045">
                    -{formatCurrency(monthlyPayment, loan.currency)}/mo
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5" data-id="element-1046">
                    Int:{' '}
                    {formatCurrency(calculateTotalInterest(loan), loan.currency)}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mb-3" data-id="element-1047">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${isJoint ? 'bg-teal-50 text-teal-700' : isFirstPartner ? 'bg-indigo-50 text-indigo-700' : 'bg-fuchsia-50 text-fuchsia-700'}`} data-id="element-1048">
                  {isJoint ? '🤝 Mutual' : loan.person}
                </span>
                <span className="text-xs text-slate-500" data-id="element-1049">
                  {formatCurrency(principal, loan.currency)} ·{' '}
                  {loan.interestRate}% · {loan.durationMonths}mo ·{' '}
                  {loan.repaymentType}
                </span>
                {loan.startYear > 0 && <span className="text-xs text-blue-500" data-id="element-1050">
                    Starts Yr {loan.startYear}
                  </span>}
              </div>
              <div className="flex items-center justify-end gap-1" data-id="element-1051">
                <Button variant="ghost" size="sm" onClick={() => onToggleActive(loan.id)} className={`px-2 ${isActive ? 'text-slate-400' : 'text-amber-500'}`} data-id="element-1052">
                  <Power className="w-4 h-4" data-id="element-1053" />
                </Button>
                {onUpdate && <Button variant="ghost" size="sm" onClick={() => setEditingId(editingId === loan.id ? null : loan.id)} className="text-slate-400 px-2" data-id="element-1054">
                    <Edit2 className="w-4 h-4" data-id="element-1055" />
                  </Button>}
                <Button variant="ghost" size="sm" onClick={() => onDelete(loan.id)} className="text-slate-400 px-2" data-id="element-1056">
                  <Trash2 className="w-4 h-4" data-id="element-1057" />
                </Button>
              </div>
            </div>;
      })}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto" data-id="element-1058">
        <table className="w-full text-sm text-left" data-id="element-1059">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-1060">
            <tr data-id="element-1061">
              <th className="px-4 py-3 font-medium" data-id="element-1062">Loan</th>
              <th className="px-4 py-3 font-medium" data-id="element-1063">Owner</th>
              <th className="px-4 py-3 font-medium" data-id="element-1064">Principal & Terms</th>
              <th className="px-4 py-3 font-medium text-right" data-id="element-1065">
                Monthly Payment
              </th>
              <th className="px-4 py-3 font-medium text-center" data-id="element-1066">Actions</th>
            </tr>
          </thead>
          <tbody data-id="element-1067">
            {loans.map(loan => <Fragment key={loan.id} data-id="element-1068">
                <LoanRow loan={loan} partnerNames={partnerNames} onDelete={onDelete} onEdit={onUpdate ? () => setEditingId(editingId === loan.id ? null : loan.id) : undefined} onToggleActive={onToggleActive} data-id="element-1069" />
                {editingId === loan.id && onUpdate && <LoanEditForm loan={loan} partnerNames={partnerNames} onSave={(id, updates) => {
              onUpdate(id, updates);
              setEditingId(null);
            }} onCancel={() => setEditingId(null)} data-id="element-1070" />}
              </Fragment>)}
          </tbody>
        </table>
      </div>
    </div>;
}